import { z } from 'zod';
import { CrmRefSchema } from '../auth/crm-registration.js';

const uuid = z.string().uuid();
const subscription = z.string().regex(/^sub_[A-Za-z0-9]+$/).max(255);
const customer = z.string().regex(/^cus_[A-Za-z0-9]+$/).max(255);

/** The public attribution code is evidence only; a dedicated server token authenticates this read. */
export const CrmBillingReadRequestSchema = z.object({
  registration_event_id: uuid,
  cabinet_id: uuid,
  creator_user_id: uuid,
  crm_ref: CrmRefSchema,
  subscription_id: subscription.optional(),
}).strict();
export type CrmBillingReadRequest = z.infer<typeof CrmBillingReadRequestSchema>;

/** Technical ownership keys are intentionally global: Intelligence checks the complete Stripe inventory. */
export const CrmBillingReadSnapshotSchema = z.object({
  registration: z.object({ event_id: uuid, cabinet_id: uuid, creator_user_id: uuid,
    crm_ref: CrmRefSchema, occurred_at: z.string().datetime({ offset: true }) }).strict(),
  cabinet: z.object({
    cabinet_id: uuid, creator_is_member: z.literal(true), billing_managed_by_cabinet_id: uuid.nullable(),
    stripe_customer_id: customer.nullable(),
    stripe_checkout_session_id: z.string().regex(/^cs_[A-Za-z0-9_]+$/).max(255).nullable(),
    subscriptions: z.array(z.object({ local_id: z.string().min(1).max(255),
      stripe_subscription_id: subscription.nullable(), stripe_customer_id: customer.nullable(),
      status: z.string().min(1).max(64), price_id: z.string().min(1).max(255) }).strict()).max(200),
    // These bounds reject an incomplete snapshot; the repository never truncates a list to fit them.
    subscription_owners: z.array(z.object({ subscription_id: subscription,
      cabinet_id: uuid, customer_id: customer.nullable() }).strict()).max(10_000),
  }).strict(),
  subscription_identity: z.object({ cabinet_id: uuid, creator_is_member: z.literal(true),
    billing_managed_by_cabinet_id: uuid.nullable(), subscription_cabinet_id: uuid.nullable(),
    subscription_customer_id: customer.nullable(), other_subscription_ids: z.array(subscription).max(200),
  }).strict().nullable(),
}).strict();
export type CrmBillingReadSnapshot = z.infer<typeof CrmBillingReadSnapshotSchema>;

/** Timestamps belong to the transport envelope, never to stable billing comparison fingerprints. */
export const CrmBillingReadResponseSchema = z.object({
  version: z.literal(1), environment: z.enum(['production', 'staging']),
  scope: CrmBillingReadRequestSchema,
  started_at: z.string().datetime({ offset: true }), completed_at: z.string().datetime({ offset: true }),
  snapshot: CrmBillingReadSnapshotSchema,
}).strict();
export type CrmBillingReadResponse = z.infer<typeof CrmBillingReadResponseSchema>;
